/* eslint-disable @typescript-eslint/no-explicit-any */
import { AttachmentTile, Text, TextField } from '@dsny/dsny-component-library';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import theme from 'src/styles/theme';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { inputHandler } from 'src/utils/InputHandler';
import { formatTitleAndArtistWithVersion } from 'src/utils/Formatter';
import {
  EditSongDescWrapper,
  ReleaseArtistNoteWrapper,
  SongInfoWrapper,
  TextFieldWrapper,
  UploadedFileWrapper,
} from './EditSongInfo.styles';
import { AddTrackObj, Artist } from '../MultiAsset.slice';
import StartTrackingSelection from '../StartTrackingSelection/StartTrackingSelection';
import ArtistSelection from '../ArtistSelection/ArtistSelection';

const TITLE_FIELD = 'title';
const START_DATE_FIELD = 'startDate';

const schema = yup.object().shape({
  title: yup.string().required(),
});

type EditSongInfoProps = {
  tracks: AddTrackObj[];
  handleTitle: (title: string) => void;
  handleArtist: (artists: Artist[]) => void;
  handleStartDate: (date: Date) => void;
  updateStartDate: boolean;
  updateArtistName: boolean;
  isCasterReleaseSong: (track: AddTrackObj) => boolean;
};

const EditSongInfo: React.FC<EditSongInfoProps> = ({
  tracks,
  handleArtist,
  handleTitle,
  handleStartDate,
  updateStartDate,
  updateArtistName,
  isCasterReleaseSong,
}) => {
  const { t } = useTranslation();
  const [selectedTracks, setSelectedTracks] = useState<AddTrackObj[]>([]);
  const [activeTracks, setActiveTracks] = useState<AddTrackObj[]>([]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [selectedDateTracksSize, setSelectedDateTracksSize] =
    useState<number>(0);

  // Banner error message
  const errorMessages = {
    artist: t('MULTI_ASSET_ERROR_ARTIST_NAME_FIELD'),
    title: t('MULTI_ASSET_ERROR_SONG_TITLE_FIELD'),
  };

  // Form validation
  const { register, setValue, getValues, trigger } = useForm({
    mode: 'all',
    resolver: yupResolver<any>(schema),
    shouldUnregister: true,
    defaultValues: {
      [TITLE_FIELD]: '',
      [START_DATE_FIELD]: '',
    },
  });

  // Caster release flow - clear start tracking date field when more then one track is selected
  const handlingStartDateInputUponSelection = (
    startDate: Date | string,
    hasUpdateStartDate: boolean
  ) => {
    if (
      selectedTracks.length > 1 &&
      selectedTracks.length !== selectedDateTracksSize
    ) {
      setSelectedDateTracksSize(selectedTracks.length);
      setValue(START_DATE_FIELD, '');
    } else if (hasUpdateStartDate) {
      setValue(START_DATE_FIELD, startDate || new Date());
    }
    if (selectedTracks.length <= 1) {
      setSelectedDateTracksSize(selectedTracks.length);
      setValue(START_DATE_FIELD, startDate || new Date());
    }
  };

  useEffect(() => {
    const title = selectedTracks.length > 0 ? '' : activeTracks[0]?.title || '';
    const startDate = activeTracks[0]?.startTrackingDate || '';

    setRefresh(!refresh);
    setValue(TITLE_FIELD, title || '');

    handlingStartDateInputUponSelection(startDate, updateStartDate);
  }, [activeTracks, updateStartDate, updateArtistName]);

  useEffect(() => {
    setActiveTracks(
      tracks.filter((track) => track.isSelected || track.isActive)
    );

    setSelectedTracks(tracks.filter((track) => track.isSelected));
  }, [tracks]);

  const isBatchEditing = (): boolean => {
    return selectedTracks.length > 0;
  };

  // Handling error message
  const hasError = (fieldId: string) => {
    if (
      activeTracks.filter((track) => track.isFileUploadError).length > 0 &&
      getValues(fieldId).trim() === ''
    ) {
      return errorMessages[fieldId as keyof typeof errorMessages];
    }
    return false;
  };

  return (
    <>
      {/* Edit Song Information */}
      <EditSongDescWrapper>
        <Text fontSize="14px" fontWeight={700} color={theme.colors.neutralW20}>
          {t('MULTI_EDIT_SONG_INFORMATION')}
        </Text>
        <Text
          ellipsis
          fontSize="12px"
          fontWeight={400}
          color={theme.colors.neutralW20}
        >
          <strong>{activeTracks.length}</strong>&nbsp;
          {t('MULTI_SONGS_SELECTED_FOR_BATCH_EDITING')}
        </Text>
      </EditSongDescWrapper>

      <SongInfoWrapper>
        <TextFieldWrapper>
          {/* Song title */}
          <TextField
            id={TITLE_FIELD}
            setting="mtr"
            labelText={t('MULTI_ASSET_SONG_TITLE')}
            type="text"
            required
            readOnly={isBatchEditing() || isCasterReleaseSong(activeTracks[0])}
            value={getValues(TITLE_FIELD)}
            {...register(TITLE_FIELD)}
            onChange={(e: ChangeEvent) => {
              inputHandler(setValue, trigger, e);
              handleTitle(getValues(TITLE_FIELD));
            }}
            error={activeTracks.length <= 1 && hasError(TITLE_FIELD)}
            wrapperStyles={{
              paddingRight: '0px',
              marginBottom: '24px',
            }}
            inputStyle={{
              height: '44px',
              borderRadius: '5px',
            }}
            labelStyle={{ fontSize: '14px', marginLeft: '12px' }}
            errorStyle={{ marginTop: '8px' }}
            ref={null}
          />

          {/* Caster release flow - Version */}
          {/* readOnly field when caster song is selected */}
          {/* hidden when a new uploaded song is selected */}
          {/* hidden when bulk editing is selected */}
          {isCasterReleaseSong(activeTracks[0]) && !isBatchEditing() && (
            <TextField
              id={TITLE_FIELD}
              setting="mtr"
              labelText={t('IMPORT_RELEASE_VERSION_FIELD')}
              type="text"
              readOnly
              value={activeTracks[0]?.releaseTrack?.version}
              wrapperStyles={{
                paddingRight: '0px',
                marginBottom: '24px',
              }}
              inputStyle={{
                height: '44px',
                borderRadius: '5px',
              }}
              labelStyle={{ fontSize: '14px' }}
              errorStyle={{ marginTop: '8px' }}
              ref={null}
            />
          )}

          {/* Artist name selection with search */}
          <ArtistSelection tracks={tracks} handleArtist={handleArtist} />
        </TextFieldWrapper>

        {/* Caster release flow - Artist note */}
        {activeTracks.length === 1 && activeTracks[0]?.releaseTrack && (
          <ReleaseArtistNoteWrapper>
            <Trans
              i18nKey="IMPORT_RELEASE_ARTIST_NOTE"
              values={{
                title: activeTracks[0]?.releaseTrack.title,
                artist: activeTracks[0]?.releaseTrack.artist,
              }}
            />
          </ReleaseArtistNoteWrapper>
        )}

        {/* Start tracking date - Day Picker */}
        <StartTrackingSelection
          handleStartDate={handleStartDate}
          displayDate={getValues(START_DATE_FIELD)}
          isCasterRelase={isCasterReleaseSong(activeTracks[0])}
          activeTracks={activeTracks}
        />

        {/* Uploaded file list */}
        {activeTracks.map((track) => {
          return (
            <UploadedFileWrapper
              key={track.S3File?.key || track.releaseTrack?.trackid}
            >
              <AttachmentTile
                attachment={{
                  id: '',
                  uploaduuid: '',
                  title: formatTitleAndArtistWithVersion(track, true),
                  filename: track?.file?.name || '',
                  size: track?.file?.size || 0,
                  type: track?.file?.type || '',
                  upload_s3_key: undefined,
                  attachid: undefined,
                }}
                onDelete={() => {
                  throw new Error('Function not implemented.');
                }}
                setting="neutral"
                displayActionIcon={false}
                isUploading={false}
                text={track?.file ? '' : ' '}
                subText={track?.file ? '' : ' '}
              />
            </UploadedFileWrapper>
          );
        })}
      </SongInfoWrapper>
    </>
  );
};

export default EditSongInfo;
